var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useMemo, useState } from 'react';
import * as React from 'react';
import clsx from 'clsx';
import { useClasses, useLayoutProps, useProps, useSkeletonLoader, } from '@hawkins/helpers';
import { useTheme } from '@hawkins/styles';
import { useHeadingLoaderProps } from '../heading';
import { Paper } from '../paper';
import { useTextLoaderProps } from '../text';
import { useCardStyles } from './card.styles';
import { CardBody } from './cardBody';
import { CardHeader } from './cardHeader';
var displayName = 'Card';
/**
 * The Card component is a generic container component. The component is basically a wrapper for
 * the `<CardHeader>`, `<CardBody>` and `<CardFooter>` components.
 *
 * Cards are fluid, you will need to set the width of the card and control the height/overflow of your
 * `<CardBody>`.
 *
 * Each one of these components can be composed together to create a card. They also each have their own
 * props that you can use to manipulate the layout and styles of each individually.
 *
 * Be sure to look at the docs for `<CardHeader>`, `<CardBody>` and `<CardFooter>` as well.
 *
 * *note* you are not limited to these components for the Card content. All children will get projected
 * inside of the Card.
 */
export var Card = React.forwardRef(function (props, ref) {
    var _a;
    // Props
    var _b = useProps(displayName, props), className = _b.className, loading = _b.loading, classesProp = _b.classes, _c = _b.component, component = _c === void 0 ? 'div' : _c, _d = _b.square, square = _d === void 0 ? false : _d, _e = _b.elevated, elevated = _e === void 0 ? false : _e, _f = _b.fullScreen, fullScreen = _f === void 0 ? false : _f, children = _b.children, _g = _b.elevation, elevation = _g === void 0 ? 1 : _g, _h = _b.hoverElevation, hoverElevation = _h === void 0 ? 2 : _h, layout = _b.layout, paperProps = _b.paperProps, styleProp = _b.style, rest = __rest(_b, ["className", "loading", "classes", "component", "square", "elevated", "fullScreen", "children", "elevation", "hoverElevation", "layout", "paperProps", "style"]);
    // Internal state
    var _j = __read(useState(elevation), 2), paperElevation = _j[0], setPaperElevation = _j[1];
    // Styles
    var hawkinsTheme = useTheme();
    var style = useLayoutProps({ layout: layout, style: styleProp });
    var baseClasses = useCardStyles();
    var classes = useClasses(displayName, baseClasses, classesProp);
    var rootClassName = clsx(classes.HAWKINS_BASE, classes.root, className, (_a = {},
        _a[classes.rootFullScreen] = fullScreen,
        _a));
    // Handlers
    var handleMouseEvent = useCallback(function (newElevation) { return function () {
        if (elevated) {
            setPaperElevation(newElevation);
        }
    }; }, [elevated]);
    // Manipulate the children and add props
    var elements = useMemo(function () {
        return React.Children.toArray(children).map(function (element) {
            if (element.type === CardHeader || element.type === CardBody) {
                return React.cloneElement(element, { fullScreen: fullScreen, square: square });
            }
            return element;
        });
    }, [children, fullScreen, square]);
    // Loading
    var fakeCardImgSkeletonLoader = useSkeletonLoader({
        loading: loading,
        loaderProps: {
            height: hawkinsTheme.hawkinsVariables.sizings.size7,
            marginBottom: hawkinsTheme.hawkinsVariables.spacings.space3,
            width: '100%',
        },
    });
    var headingSkeletonLoaderProps = useHeadingLoaderProps();
    var headingSkeleton = useSkeletonLoader({
        loading: loading,
        loaderProps: __assign(__assign({}, headingSkeletonLoaderProps), { marginBottom: hawkinsTheme.hawkinsVariables.spacings.space3, width: '25%' }),
    });
    var textLoadingSkeletonProps = useTextLoaderProps({ level: -1 });
    var textLoadingSkeleton = useSkeletonLoader({
        loading: loading,
        loaderProps: __assign(__assign({}, textLoadingSkeletonProps), { marginBottom: hawkinsTheme.hawkinsVariables.spacings.space1 }),
    });
    var textLoadingSkeletonLast = useSkeletonLoader({
        loading: loading,
        loaderProps: __assign(__assign({}, textLoadingSkeletonProps), { width: '35%' }),
    });
    var hasAnyLoader = fakeCardImgSkeletonLoader || headingSkeleton || textLoadingSkeleton;
    if (hasAnyLoader) {
        var Component = component;
        return (_jsxs(Component, __assign({ className: rootClassName, style: style }, rest, { children: [fakeCardImgSkeletonLoader, headingSkeleton, textLoadingSkeleton, textLoadingSkeleton, textLoadingSkeleton, textLoadingSkeleton, textLoadingSkeletonLast] })));
    }
    return (_jsx(Paper, __assign({}, paperProps, { className: rootClassName, component: component, elevation: paperElevation, onMouseEnter: handleMouseEvent(hoverElevation), onMouseLeave: handleMouseEvent(elevation), ref: ref, style: style, square: square }, rest, { children: elements })));
});
Card.displayName = displayName;
